import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';
import { fontsList } from 'pxc-editor-material-svg-rendering/constants';
import { colors } from '@/globalStyles';
import {
  TOP,
  MIDDLE,
  BOTTOM,
  LEFT,
  CENTER,
  RIGHT,
  DEFAULT_SHAPE_PARAMS,
  TEXTFIELDMATRIX,
  LINE_SPACE_FACTOR_VALUES,
  MIN_LINE_SPACE_FACTOR,
  MAX_LINE_SPACE_FACTOR,
} from '@/constants';
import {
  getElementsByQuerySelectorAll,
  defineSectionContent,
  getElementByGetElementById,
  insertSymbolAvailable,
} from '@/helpers';
import {
  isColorOptionAvailable,
  isColorPrinterAvailable,
} from '@/helpers/printers';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { convertPercentToValue, convertValueToPercent } from '@/helpers/graphicContent';
import {setGroupSelectionTypeThunk} from '@/actions';
import UsefullNumericSizeInput from '@/components/NumericInput/UsefullNumericSizeInput';
import InlineButtonsGroup from '@/components/InlineButtonsGroup';
import ColorSelectionPanel from '@/components/ColorSelectionPanel/ColorSelectionPanel';
import CheckButton from '@/components/CheckButton/CheckButton';
import { Tooltip, MatrixIcon } from '@/components';
import IconGrid from '@/icons/icon-grid.svg';
import IconGridSelected from '@/icons/icon-grid-selected.svg';
import IconTextStyleBold from '@/icons/icon-text-style-bold.svg';
import IconTextStyleBoldSelected from '@/icons/icon-text-style-bold-selected.svg';
import IconTextStyleItalic from '@/icons/icon-text-style-italic.svg';
import IconTextStyleItalicSelected from '@/icons/icon-text-style-italic-selected.svg';
import IconTextStyleUnderline from '@/icons/icon-text-style-underline.svg';
import IconTextStyleUnderlineSelected from '@/icons/icon-text-style-underline-selected.svg';
import IconHorizontalAlignmentLeft from '@/icons/icon-horizontal-alignment-left.svg';
import IconHorizontalAlignmentLeftSelected from '@/icons/icon-horizontal-alignment-left-selected.svg';
import IconHorizontalAlignmentCenter from '@/icons/icon-horizontal-alignment-center.svg';
import IconHorizontalAlignmentCenterSelected from '@/icons/icon-horizontal-alignment-center-selected.svg';
import IconHorizontalAlignmentRight from '@/icons/icon-horizontal-alignment-right.svg';
import IconHorizontalAlignmentRightSelected from '@/icons/icon-horizontal-alignment-right-selected.svg';
import IconVerticalAlignmentTop from '@/icons/icon-vertical-alignment-top.svg';
import IconVerticalAlignmentTopSelected from '@/icons/icon-vertical-alignment-top-selected.svg';
import IconVerticalAlignmentMiddle from '@/icons/icon-vertical-alignment-middle.svg';
import IconVerticalAlignmentMiddleSelected from '@/icons/icon-vertical-alignment-middle-selected.svg';
import IconVerticalAlignmentBottom from '@/icons/icon-vertical-alignment-bottom.svg';
import IconVerticalAlignmentBottomSelected from '@/icons/icon-vertical-alignment-bottom-selected.svg';
import IconSelectArrowDown from '@/icons/icon-select-arrow-down.svg';
import IconSelectChecked from '@/icons/icon-select-checked.svg';
import IconEmpty from '@/icons/icon-empty.svg';
import IconMinus from '@/icons/icon-cmme-minus.svg';
import IconPlus from '@/icons/icon-cmme-plus.svg';
import IconSectionSplitLeft from '../Icon/CmmeIcons/IconSectionSplitLeft';
import IconSectionSplitDown from '../Icon/CmmeIcons/IconSectionSplitDown';
import IconSectionAdd from '../Icon/CmmeIcons/IconSectionAdd';
import IconRotateLeft from '../Icon/CmmeIcons/IconRotateLeft';
import IconRotateRight from '../Icon/CmmeIcons/IconRotateRight';
import IconFlipHorizontal from '../Icon/CmmeIcons/IconFlipHorizontal';
import IconFlipVertical from '../Icon/CmmeIcons/IconFlipVertical';
import IconStroke from '../Icon/CmmeIcons/IconStroke';
import IconStrokeDashed from '../Icon/CmmeIcons/IconStrokeDashed';
import IconShapeRectangleSoftEdges from '../Icon/CmmeIcons/IconShapeRectangleSoftEdges';
import IconFill from '../Icon/CmmeIcons/IconFill';
import IconMatrixAddRowBottom from '../Icon/CmmeIcons/IconMatrixAddRowBottom';
import IconMatrixAddRowTop from '../Icon/CmmeIcons/IconMatrixAddRowTop';
import IconMatrixAddCellLeft from '../Icon/CmmeIcons/IconMatrixAddCellLeft';
import IconMatrixAddCellRight from '../Icon/CmmeIcons/IconMatrixAddCellRight';
import IconMatrixMerge from '../Icon/CmmeIcons/IconMatrixMerge';
import IconMatrixUnmerge from '../Icon/CmmeIcons/IconMatrixUnmerge';
import IconMatrixBorders from '../Icon/CmmeIcons/IconMatrixBorders';
import IconMatrixAddSeparatorLeft from '../Icon/CmmeIcons/IconMatrixAddSeparatorLeft';
import IconMatrixAddSeparatorRight from '../Icon/CmmeIcons/IconMatrixAddSeparatorRight';
import IconCmmeCheckbox from '../Icon/CmmeIcons/IconCmmeCheckbox';
import IconCmmeCheckboxSelected from '../Icon/CmmeIcons/IconCmmeCheckboxSelected';
import Expandable from './Expandable';
import InsertBar from './InsertBar';
import SymbolList from './SymbolList';
import {
  ItemsContainer,
  GridContainer,
  Label,
  Icon,
  GroupItem,
  HeaderContainer,
  TextSelectButton,
  Icon24,
  SelectionItems,
  SelectionItem,
  Icon16,
  OptionButtonStyled,
} from './styles';

const toFixedX = (value, decimals = 1) => {
  const result =
    Math.round((0 + value) * Math.pow(10, decimals)) / Math.pow(10, decimals);
  return result;
};

const getVerticalAlignmentValue = section => {
  if (section && !!section?.image) {
    return section?.image.verticalAlignment;
  } else if (section && !!section?.glyph) {
    return section?.glyph.verticalAlignment;
  } else {
    if (section?.verticalAlignment) {
      return section.verticalAlignment.toUpperCase();
    }
    return MIDDLE;
  }
};

const getHorizontalAlignmentValue = section => {
  if (section && !!section?.image) {
    return section?.image.horizontalAlignment;
  } else if (section && !!section?.glyph) {
    return section?.glyph.horizontalAlignment;
  } else {
    if (section?.horizontalAlignment) {
      return section.horizontalAlignment.toUpperCase();
    }
    return CENTER;
  }
};

const SideBar = props => {
  const {
    pages,
    pagesGroups,
    groupSelectionType,
    userSizeUnit,
    customFontColors,
    addCustomFontColor,
    isGridDisplayed,
    isShowInsertModal,
    hasPages,
    areShieldsSelected,
    areSectionsSelected,
    isMaterialWithLaserParameters,
    selectedSection,
    isMatrix,
    cmmeStates,
    setCmmeStates,
    sectionsSelection,
    sections,
    gridWidth,
    updateSelectedSectionsProps,
    updateSectionShapeProps,
    addSectionAngle,
    addSectionMirror,
    areShieldControlsDisabled,
    areSectionControlsDisabled,
    selectedShield,
    splitSectionVertical,
    splitSectionHorizontal,
    addNewSections,
    setGridDisplayState,
    setLaserOutputQualityValueToSectionThunk,
    setGridWidth,
    mergeMatrixCells,
    splitMatrixCell,
    appendMatrixCell,
    appendMatrixRow,
    appendMatrixSeparator,
    setIsShowMatrixBorderThunk,
    preselectedPrinter,
    printerList,
    setShowInsertModalState,
    isFlipVerticalAvailable,
    onMatrixCellWidthChangeClb,
    disableMatrixCellWidth,
  } = props;
  const matfileTemplate = getBuildedMatfileTemplate();
  const isShapeRectangleSoftEdgesActive = !!selectedSection?.shape?.softEdges;
  const shapeRectangleSoftEdgesSize =
    selectedSection?.shape?.cornerRadius || DEFAULT_SHAPE_PARAMS.CORNER_RADIUS;
  const isShapeStrokeActive = !!selectedSection?.shape?.hasStroke;
  const shapeStrokeSize =
    selectedSection?.shape?.strokeWidth || DEFAULT_SHAPE_PARAMS.STROKE_WIDTH;
  const isShapeDashed = !!selectedSection?.shape?.isDashed;
  const isShapeFillActive = !!selectedSection?.shape?.isFilled;
  const sectionsSelectionCount = Object.values(sectionsSelection)?.length || 0;
  const sideBarStates = cmmeStates.sideBarStates;
  const intl = useIntl();
  const [cornersCount, setcornersCount] = useState(0);
  const [isVerticalSelection, setIsVerticalSelection] = useState(false);
  const [isSeparatorExists, setIsSeparatorExists] = useState(false);
  const [selectedInsertBar, setSelectedInsertBar] = useState(null);
  const [textSelectOpen, setTextSelectOpen] = useState(false);
  const [sidebarScrollPosition, setSidebarScrollPosition] = useState(0);
  const dispatch = useDispatch();
  const isViewSectionOpenRef = useRef(false);
  const disable_appendMatrixCell =
    areSectionControlsDisabled ||
    sectionsSelectionCount > 1 ||
    isVerticalSelection ||
    isSeparatorExists;
  const disable_appendMatrixRow =
    areSectionControlsDisabled || sectionsSelectionCount > 1;
  const disable_mergeMatrixCells =
    (areShieldControlsDisabled && areSectionControlsDisabled) ||
    sectionsSelectionCount < 2 ||
    cornersCount !== 4;
  const disable_splitMatrixCell =
    (areShieldControlsDisabled && areSectionControlsDisabled) ||
    sectionsSelectionCount !== 1 ||
    !(selectedSection?.children?.length > 1);
  const leftSeparatorDisabled = sectionsSelectionCount > 1;
  const rightSeparatorDisabled = sectionsSelectionCount > 1;
  const { transforms } = selectedShield ? getAllLayers(selectedShield.id, pages).group.textStyle : { transforms: { angle: 0 } };
  const matrixRotation = transforms?.angle > 0;
  const preselectedFont = fontsList?.length ? fontsList[0] : '';
  const colorOptionAvailable =
    isColorOptionAvailable(preselectedPrinter) &&
    isColorPrinterAvailable(printerList);
  const fontFamily = selectedSection?.font || preselectedFont;
  const fontSize = selectedSection?.fontSize || '';
  const fontWidthFactor = selectedSection?.fontWidthFactor || '';
  const lineSpaceFactor = selectedSection?.lineSpaceFactor || '';
  const isBold = selectedSection?.isBold || false;
  const isItalic = selectedSection?.isItalic || false;
  const isUnderline = selectedSection?.isUnderline || false;
  const horizontalAlignment = getHorizontalAlignmentValue(selectedSection);
  const verticalAlignment = getVerticalAlignmentValue(selectedSection);
  const sizeUnitTranslate = intl.formatMessage({
    id: `app.sizeUnits.${userSizeUnit}`,
    defaultMessage: userSizeUnit,
  });
  const graphicFormattingSizeValue = selectedSection?.graphicFormattingScale
    ? convertValueToPercent(selectedSection.graphicFormattingScale)
    : '';
  const { shield } = getAllLayers((selectedSection?.id || ''), pages);
  const fontSizeLimitations = {
    minval: shield?.isMatrix ? 0.1 : 1.6,
    maxval: shield?.isMatrix ? Infinity : 100
  };
  const textSizeTranslate = `${intl.formatMessage({
    id: 'cmme.size',
    defaultMessage: 'Size',
  })} (${sizeUnitTranslate})`;
  const sectionContents = defineSectionContent({
    sections,
    sectionsSelection,
    selectedSection,
    pages,
  });
  const isInsertSymbolAvailable = sectionContents.isInsertSymbolAvailable;
  const isVisible = {
    VIEW: true,
    TEXT: sectionContents.isTextOptionAvailable,
    ALIGNMENT: sectionContents.isAlignmentOptionAvailable,
    SECTIONS: sectionContents.isSectionsOptionAvailable,
    ROTATEFLIP: sectionContents.isRotateAndFlipOptionAvailable,
    MATRIX: sectionContents.isMatrixOptionAvailable,
    SEPARATOR: sectionContents.isSeparatorOptionAvailable,
    SHAPERECTANGLE: sectionContents.isRectangleShapeOptionAvailable,
    SHAPEELLIPSE: sectionContents.isEllipseShapeOptionAvailable,
    LASER: !isMatrix && isMaterialWithLaserParameters,
    SYMBOL: isInsertSymbolAvailable,
    GRAPHIC: sectionContents.isGraphicOptionAvailable,
  };

  useEffect(() => {
    const matrixSelectionCorners = getElementsByQuerySelectorAll(
      '.matrixselection_corner'
    )?.length;
    if (
      typeof matrixSelectionCorners === 'number' &&
      matrixSelectionCorners !== cornersCount
    ) {
      setcornersCount(matrixSelectionCorners);
    }
  });

  useEffect(() => {
    setCurrentDefaults();
    setCmmeStates({
      ...cmmeStates,
      sideBarStates: { ...sideBarStates, ['TEXT']: !insertSymbolAvailable() },
    });
  }, [matfileTemplate?.name]);

  const getScrollPos = () => {
    setcornersCount(
      getElementsByQuerySelectorAll('.matrixselection_corner')?.length
    );
    const scrollableContainer = getElementByGetElementById(
      'sidebar-scrollable-container'
    );
    const scrollableContainerY = scrollableContainer?.scrollTop;

    setSidebarScrollPosition(scrollableContainerY);
  };

  const updateScrollPos = () => {
    setcornersCount(
      getElementsByQuerySelectorAll('.matrixselection_corner')?.length
    );
    const scrollableContainer = getElementByGetElementById(
      'sidebar-scrollable-container'
    );
    scrollableContainer.scrollTop = sidebarScrollPosition;
  };

  const setCurrentDefaults = () => {
    updateScrollPos();

    if (selectedSection) {
      const { page, shield } = getAllLayers(selectedSection?.id, pages);
      const { transforms } = getAllLayers(shield.id, pages).group.textStyle;
      const matrixRotation = transforms?.angle > 0;
      if (sectionsSelectionCount > 1) {
        if (
          shield.isMatrix &&
          page?.sections &&
          Object.values(sectionsSelection)?.length
        ) {
          const y = matrixRotation ? 'x' : 'y';
          const rowsY = [
            ...new Set(
              Object.values(sectionsSelection).map(s =>
                toFixedX(page.sections[s.id][y], 5)
              )
            ),
          ];
          setIsVerticalSelection(rowsY?.length > 1);
        }
      } else {
        setIsVerticalSelection(false);
      }
      if (page.sections) {
        let foundSeparator = false;
        Object.values(page.sections)
          .filter(s => s.shieldId === shield.id)
          .forEach(section => {
            if (section?.type === 'separator') {
              foundSeparator = true;
            }
          });
        setIsSeparatorExists(foundSeparator);
      }
    }
  };

  const handleUpdateExpandableStates = (id, state) => {
    setCmmeStates({
      ...cmmeStates,
      sideBarStates: { ...sideBarStates, [id]: state },
    });
  };

  const handleViewSectionChange = (id, state) => {
    if (!isViewSectionOpenRef.current) {
      isViewSectionOpenRef.current = true;
    }
    handleUpdateExpandableStates(id, state);
  };

  const handleGroupSelectionType = newGroupSelectionType => {
    dispatch(setGroupSelectionTypeThunk(newGroupSelectionType));
  };

  const handleSetIsLaserOutputCutout = value => {
    setLaserOutputQualityValueToSectionThunk(selectedSection, {
      cutout: value,
    });
  };

  const handleChangeFont = value => {
    setTextSelectOpen(false);
    updateSelectedSectionsProps({ font: value });
  };

  const toggleGrid = () => {
    setGridDisplayState(!isGridDisplayed);
  };

  const toggleShapeRectangleSoftEdges = () => {
    updateSectionShapeProps({ softEdges: !isShapeRectangleSoftEdgesActive });
  };

  const toggleShapeStroke = () => {
    updateSectionShapeProps({ hasStroke: !isShapeStrokeActive });
  };

  const toggleShapeFill = () => {
    updateSectionShapeProps({ isFilled: !isShapeFillActive });
  };

  const onFontSizeChangeClb = value => {
    updateSelectedSectionsProps({ fontSize: value });
  };

  const onFontWidthFactorChangeClb = value => {
    updateSelectedSectionsProps({ fontWidthFactor: value });
  };

  const onLineSpaceFactorChangeClb = (value) => {
    updateSelectedSectionsProps({ lineSpaceFactor: value });
  };

  const handleSetIsBold = value => {
    updateSelectedSectionsProps({ isBold: value });
  };

  const handleSetIsItalic = value => {
    updateSelectedSectionsProps({ isItalic: value });
  };

  const handleSetIsUnderline = value => {
    updateSelectedSectionsProps({ isUnderline: value });
  };

  const handleSetVerticalAlignment = value => {
    updateSelectedSectionsProps({ verticalAlignment: value.toLowerCase() });
  };

  const handleSetHorizontalAlignment = value => {
    updateSelectedSectionsProps({ horizontalAlignment: value.toLowerCase() });
  };

  const onShapeRectangleSoftEdgesSizeChangeClb = value => {
    updateSectionShapeProps({ cornerRadius: value });
  };

  const onShapeStrokeSizeChangeClb = value => {
    updateSectionShapeProps({ strokeWidth: value });
  };

  const handleSetIsShapeDashed = value => {
    updateSectionShapeProps({ isDashed: value });
  };

  const rotateLeft = () => {
    addSectionAngle(-90);
  };

  const rotateRight = () => {
    addSectionAngle(90);
  };

  const flipHorizontal = () => {
    addSectionMirror('x');
  };

  const flipVertical = () => {
    addSectionMirror('y');
  };

  const matrixAddRowBottom = () => {
    appendMatrixRow('bottom');
  };

  const matrixAddRowTop = () => {
    appendMatrixRow('top');
  };

  const matrixAddCellRight = () => {
    appendMatrixCell('right', 'text');
  };

  const matrixAddCellLeft = () => {
    appendMatrixCell('left', 'text');
  };

  const matrixMerge = () => {
    mergeMatrixCells();
  };

  const matrixUnmerge = () => {
    splitMatrixCell();
  };

  const toggleMatrixBorders = () => {
    setIsShowMatrixBorderThunk(selectedShield);
  };

  const matrixAddSeparatorlLeft = () => {
    if (!leftSeparatorDisabled) {
      appendMatrixSeparator('left');
    }
  };

  const matrixAddSeparatorlRight = () => {
    if (!rightSeparatorDisabled) {
      appendMatrixSeparator('right');
    }
  };

  const handleFontColorChange = value => {
    updateSelectedSectionsProps({ fontColor: value });
  };

  const handleShowMatrixConfig = () => {
    setSelectedInsertBar(TEXTFIELDMATRIX);
    setShowInsertModalState(true);
  };

  const handleShapeStrokeColorChange = value => {
    updateSelectedSectionsProps({ shapeStrokeColor: value });
  };

  const handleShapeFillColorChange = value => {
    updateSelectedSectionsProps({ shapeFillColor: value });
  };

  const handleGraphicFormattingSizeChange = value => {
    updateSelectedSectionsProps({ graphicFormattingScale: convertPercentToValue(value) });
  };

  const gridToggleList = [
    {
      icon: (
        <Icon
          src={isGridDisplayed ? IconGridSelected : IconGrid}
          onMouseOver={e => (e.target.src = IconGridSelected)}
          onMouseOut={e =>
            (e.target.src = isGridDisplayed ? IconGridSelected : IconGrid)
          }
        />
      ),
      selected: isGridDisplayed,
      onClick: () => toggleGrid(),
    },
  ];

  const shapeRectangleSoftEdgesToggleList = [
    {
      icon: (
        <IconShapeRectangleSoftEdges
          fill={isShapeRectangleSoftEdgesActive ? colors.primary : colors.nero}
        />
      ),
      selected: isShapeRectangleSoftEdgesActive,
      onClick: () => toggleShapeRectangleSoftEdges(),
    },
  ];

  const shapeStrokeToggleList = [
    {
      icon: (
        <IconStroke fill={isShapeStrokeActive ? colors.primary : colors.nero} />
      ),
      selected: isShapeStrokeActive,
      onClick: () => toggleShapeStroke(),
    },
  ];

  const shapeFillToggleList = [
    {
      icon: (
        <IconFill fill={isShapeFillActive ? colors.primary : colors.nero} />
      ),
      selected: isShapeFillActive,
      onClick: () => toggleShapeFill(),
    },
  ];

  const fontsStylesList = [
    {
      icon: (
        <Icon
          src={isBold ? IconTextStyleBoldSelected : IconTextStyleBold}
          onMouseOver={e => (e.target.src = IconTextStyleBoldSelected)}
          onMouseOut={e =>
            (e.target.src = isBold
              ? IconTextStyleBoldSelected
              : IconTextStyleBold)
          }
        />
      ),
      selected: isBold,
      onClick: () => handleSetIsBold(!isBold),
    },
    {
      icon: (
        <Icon
          src={isItalic ? IconTextStyleItalicSelected : IconTextStyleItalic}
          onMouseOver={e => (e.target.src = IconTextStyleItalicSelected)}
          onMouseOut={e =>
            (e.target.src = isItalic
              ? IconTextStyleItalicSelected
              : IconTextStyleItalic)
          }
        />
      ),
      selected: isItalic,
      onClick: () => handleSetIsItalic(!isItalic),
    },
    {
      icon: (
        <Icon
          src={
            isUnderline
              ? IconTextStyleUnderlineSelected
              : IconTextStyleUnderline
          }
          onMouseOver={e => (e.target.src = IconTextStyleUnderlineSelected)}
          onMouseOut={e =>
            (e.target.src = isUnderline
              ? IconTextStyleUnderlineSelected
              : IconTextStyleUnderline)
          }
        />
      ),
      selected: isUnderline,
      onClick: () => handleSetIsUnderline(!isUnderline),
    },
  ];

  const shapeDashTypeList = [
    {
      icon: <IconStroke fill={isShapeDashed ? colors.nero : colors.primary} />,
      selected: !isShapeDashed,
      onClick: () => handleSetIsShapeDashed(false),
    },
    {
      icon: (
        <IconStrokeDashed fill={isShapeDashed ? colors.primary : colors.nero} />
      ),
      selected: isShapeDashed,
      onClick: () => handleSetIsShapeDashed(true),
    },
  ];

  const horAlList = [
    {
      icon: (
        <Icon
          src={
            horizontalAlignment === LEFT
              ? IconHorizontalAlignmentLeftSelected
              : IconHorizontalAlignmentLeft
          }
          style={{ width: '20px', height: '20px' }}
          onMouseOver={e =>
            (e.target.src = IconHorizontalAlignmentLeftSelected)
          }
          onMouseOut={e =>
            (e.target.src =
              horizontalAlignment === LEFT
                ? IconHorizontalAlignmentLeftSelected
                : IconHorizontalAlignmentLeft)
          }
        />
      ),
      selected: horizontalAlignment === LEFT,
      onClick: () => handleSetHorizontalAlignment(LEFT),
    },
    {
      icon: (
        <Icon
          src={
            horizontalAlignment === CENTER
              ? IconHorizontalAlignmentCenterSelected
              : IconHorizontalAlignmentCenter
          }
          style={{ width: '20px', height: '20px' }}
          onMouseOver={e =>
            (e.target.src = IconHorizontalAlignmentCenterSelected)
          }
          onMouseOut={e =>
            (e.target.src =
              horizontalAlignment === CENTER
                ? IconHorizontalAlignmentCenterSelected
                : IconHorizontalAlignmentCenter)
          }
        />
      ),
      selected: horizontalAlignment === CENTER,
      onClick: () => handleSetHorizontalAlignment(CENTER),
    },
    {
      icon: (
        <Icon
          src={
            horizontalAlignment === RIGHT
              ? IconHorizontalAlignmentRightSelected
              : IconHorizontalAlignmentRight
          }
          style={{ width: '20px', height: '20px' }}
          onMouseOver={e =>
            (e.target.src = IconHorizontalAlignmentRightSelected)
          }
          onMouseOut={e =>
            (e.target.src =
              horizontalAlignment === RIGHT
                ? IconHorizontalAlignmentRightSelected
                : IconHorizontalAlignmentRight)
          }
        />
      ),
      selected: horizontalAlignment === RIGHT,
      onClick: () => handleSetHorizontalAlignment(RIGHT),
    },
  ];

  const vertAlList = [
    {
      icon: (
        <Icon
          src={
            verticalAlignment === TOP
              ? IconVerticalAlignmentTopSelected
              : IconVerticalAlignmentTop
          }
          style={{ width: '20px', height: '20px' }}
          onMouseOver={e => (e.target.src = IconVerticalAlignmentTopSelected)}
          onMouseOut={e =>
            (e.target.src =
              verticalAlignment === TOP
                ? IconVerticalAlignmentTopSelected
                : IconVerticalAlignmentTop)
          }
        />
      ),
      selected: verticalAlignment === TOP,
      onClick: () => handleSetVerticalAlignment(TOP),
    },
    {
      icon: (
        <Icon
          src={
            verticalAlignment === MIDDLE
              ? IconVerticalAlignmentMiddleSelected
              : IconVerticalAlignmentMiddle
          }
          style={{ width: '20px', height: '20px' }}
          onMouseOver={e =>
            (e.target.src = IconVerticalAlignmentMiddleSelected)
          }
          onMouseOut={e =>
            (e.target.src =
              verticalAlignment === MIDDLE
                ? IconVerticalAlignmentMiddleSelected
                : IconVerticalAlignmentMiddle)
          }
        />
      ),
      selected: verticalAlignment === MIDDLE,
      onClick: () => handleSetVerticalAlignment(MIDDLE),
    },
    {
      icon: (
        <Icon
          src={
            verticalAlignment === BOTTOM
              ? IconVerticalAlignmentBottomSelected
              : IconVerticalAlignmentBottom
          }
          style={{ width: '20px', height: '20px' }}
          onMouseOver={e =>
            (e.target.src = IconVerticalAlignmentBottomSelected)
          }
          onMouseOut={e =>
            (e.target.src =
              verticalAlignment === BOTTOM
                ? IconVerticalAlignmentBottomSelected
                : IconVerticalAlignmentBottom)
          }
        />
      ),
      selected: verticalAlignment === BOTTOM,
      onClick: () => handleSetVerticalAlignment(BOTTOM),
    },
  ];

  const gridControlTools = [
    <GridContainer
      key="gridControlTools-grid"
      $gridTemplateColumns="1fr 2fr 1fr"
    >
      <Label key="gridControlTools-label">
        <FormattedMessage id="cmme.grid" defaultMessage="Grid" />
      </Label>
      <div></div>
      <div></div>
      <div className="inputContainer">
        <InlineButtonsGroup items={gridToggleList} />
      </div>
      {isGridDisplayed ? (
        <UsefullNumericSizeInput
          className="inputContainer"
          $gridTemplateColumns="4fr 5fr 4fr"
          $borderRadius="4px"
          onChange={setGridWidth}
          value={gridWidth}
          units={userSizeUnit}
          iconMinus={IconMinus}
          iconPlus={IconPlus}
          hoverColor={colors.whiteSmoke}
          selectedColor={colors.primary}
          stepMm={0.1}
          minval={0.1}
        />
      ) : (
        <div></div>
      )}
      <div></div>
    </GridContainer>,
  ];

  const laserControlTools = [
    <GridContainer
      key="gridControlTools-grid-laser"
      $gridTemplateColumns="1fr 1fr"
    >
      <div
        className="checkBoxContainer cursor-pointer"
        onClick={handleSetIsLaserOutputCutout.bind(
          null,
          !selectedSection?.laserOutputQuality?.cutout
        )}
      >
        <div style={{ display: 'flex' }}>
          {!!selectedSection?.laserOutputQuality?.cutout ? (
            <IconCmmeCheckboxSelected fill={colors.primary} />
          ) : (
            <IconCmmeCheckbox />
          )}
        </div>
        <div className="checkboxLabel">
          <FormattedMessage id="cmme.cutout" defaultMessage="Cutout" />
        </div>
      </div>
    </GridContainer>,
  ];

  const groupsSelectionControlTools = [
    ...(matfileTemplate?.shields_number > 1
      ? [
          <Box
            key="groupsSelectionControlTools"
            sx={{ display: 'grid', rowGap: '10px' }}
          >
            <Label>
              <FormattedMessage
                id="cmme.groupsSelection"
                defaultMessage="Groups selection"
              />
            </Label>
            <GridContainer $gridTemplateColumns="1fr 1fr 1fr 1fr">
              {pagesGroups.map(group => (
                <div
                  className="inputContainer"
                  key={group.uid + '-input-container'}
                >
                  <GroupItem key={group.uid}>
                    <CheckButton
                      onClick={handleGroupSelectionType.bind(null, group.id)}
                      backgroundColor={group.group_color}
                      checked={groupSelectionType.some(el => el === group.id)}
                    />
                  </GroupItem>
                </div>
              ))}
            </GridContainer>
          </Box>,
        ]
      : []),
  ];

  const textControlTools = [
    <GridContainer key="textControlTools-grid-font" $gridTemplateColumns="1fr">
      <Label key="textControlTools-label-font">
        <FormattedMessage
          id="app.editor.menu.format.fontFamily"
          defaultMessage="Font style"
        />
      </Label>
      <Tooltip
        className="Select inputContainer"
        type="option-panel"
        placement="bottom-end"
        open={textSelectOpen}
        handleClose={() => setTextSelectOpen(false)}
        title={
          <SelectionItems>
            <GridContainer
              key="textControlTools-grid-font"
              style={{ width: '100%', paddingBottom: '10px' }}
              $gridTemplateColumns="1fr"
            >
              {fontsList?.map(f => (
                <SelectionItem
                  onClick={() => handleChangeFont(f)}
                  $selected={f === fontFamily}
                  className="MenuItem"
                  key={f}
                  value={f}
                >
                  <div>{f}</div>
                  <Icon16
                    style={{ marginLeft: '20px' }}
                    src={f === fontFamily ? IconSelectChecked : IconEmpty}
                  />
                </SelectionItem>
              ))}
            </GridContainer>
          </SelectionItems>
        }
      >
        <HeaderContainer onClick={() => setTextSelectOpen(!textSelectOpen)}>
          <TextSelectButton>
            <div>{fontFamily}</div>
            <Icon24
              src={IconSelectArrowDown}
              style={{
                transform: textSelectOpen ? 'rotate(180deg)' : '',
              }}
            />
          </TextSelectButton>
        </HeaderContainer>
      </Tooltip>
    </GridContainer>,
    <GridContainer
      key="textControlTools-grid-size-style"
      $gridTemplateColumns="1fr 1fr"
      $gridTemplateRows="min-content 1fr min-content 1fr"
    >
      <Label key="textControlTools-label-size">{textSizeTranslate}</Label>
      <Label key="textControlTools-label-style">
        <FormattedMessage id="cmme.style" defaultMessage="Style" />
      </Label>
      <UsefullNumericSizeInput
        className="inputContainer"
        $borderRadius="4px"
        $gridTemplateColumns="4fr 5fr 4fr"
        onChange={onFontSizeChangeClb}
        value={fontSize}
        units={userSizeUnit}
        iconMinus={IconMinus}
        iconPlus={IconPlus}
        hoverColor={colors.whiteSmoke}
        selectedColor={colors.primary}
        stepMm={0.1}
        {...fontSizeLimitations}
      />
      <div className="inputContainer">
        <InlineButtonsGroup items={fontsStylesList} />
      </div>
      <Label key="textControlTools-label-width-factor">
        <FormattedMessage
          id="cmme.widthFactor"
          defaultMessage="Width factor"
        />
      </Label>
      <Label key="textControlTools-label-line-space-factor">
        <FormattedMessage
          id="cmme.lineSpaceFactor"
          defaultMessage="Line space factor"
        />
      </Label>
      <UsefullNumericSizeInput
        className="inputContainer"
        $borderRadius="4px"
        $gridTemplateColumns="4fr 5fr 4fr"
        onChange={onFontWidthFactorChangeClb}
        value={fontWidthFactor}
        iconMinus={IconMinus}
        iconPlus={IconPlus}
        hoverColor={colors.whiteSmoke}
        selectedColor={colors.primary}
        stepMm={0.1}
        minval={0.1}
        maxval={2}
        units={userSizeUnit}
      />
      <UsefullNumericSizeInput
        className="inputContainer"
        $borderRadius="4px"
        $gridTemplateColumns="4fr 5fr 4fr"
        onChange={onLineSpaceFactorChangeClb}
        value={lineSpaceFactor}
        iconMinus={IconMinus}
        iconPlus={IconPlus}
        hoverColor={colors.whiteSmoke}
        selectedColor={colors.primary}
        stepMm={0.5}
        steps={LINE_SPACE_FACTOR_VALUES}
        minval={MIN_LINE_SPACE_FACTOR}
        maxval={MAX_LINE_SPACE_FACTOR}
        units={userSizeUnit}
      />
    </GridContainer>,
    ...(colorOptionAvailable
      ? [
          <GridContainer
            key="textControlTools-grid-font-color"
            $gridTemplateColumns="1fr"
          >
            <Label key="textControlTools-label-font-color">
              <FormattedMessage
                id="app.editor.menu.format.fontColor"
                defaultMessage="Font color"
              />
            </Label>
            <div className="inputContainer">
              <ColorSelectionPanel
                onChange={handleFontColorChange}
                onAddNewColor={addCustomFontColor}
                colorsList={null}
                additionalColors={customFontColors}
                selected={selectedSection?.fontColor}
                colorItemClassName="ColorItemRect"
              />
            </div>
          </GridContainer>,
        ]
      : []),
  ];

  const aligmentControlTools = [
    <GridContainer
      key="aligmentControlTools-grid"
      $gridTemplateColumns="1fr 1fr"
    >
      <Label key="aligmentControlTools-label-horizontal">
        <FormattedMessage
          id="app.editor.sideMenu.expansionPanel.uploadImage.horizontal"
          defaultMessage="Horizontal"
        />
      </Label>
      <Label key="aligmentControlTools-label-vertical">
        <FormattedMessage
          id="app.editor.sideMenu.expansionPanel.uploadImage.vertical"
          defaultMessage="Vertical"
        />
      </Label>
      <div className="inputContainer">
        <InlineButtonsGroup items={horAlList} />
      </div>
      <div className="inputContainer">
        <InlineButtonsGroup items={vertAlList} />
      </div>
    </GridContainer>,
  ];

  const sectionsControlTools = [
    <GridContainer
      key="sectionsControlTools-grid"
      $gridTemplateColumns="1fr 1fr 1fr 1fr"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Label key="sectionsControlTools-label-split-left">
          <FormattedMessage
            id="app.editor.sidebar.splitVertical"
            defaultMessage="Split vertically"
          />
        </Label>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Label key="sectionsControlTools-label-split-down">
          <FormattedMessage
            id="app.editor.sidebar.splitHorizontal"
            defaultMessage="Split horizontally"
          />
        </Label>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Label key="sectionsControlTools-label-add-section">
          <FormattedMessage
            id="app.editor.sidebar.addSection"
            defaultMessage="Add section"
          />
        </Label>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Label key="sectionsControlTools-label-add-matrix">
          <FormattedMessage
            id="app.editor.sideMenu.header.matrix"
            defaultMessage="Matrix"
          />
        </Label>
      </Box>

      <div onClick={splitSectionVertical} className="inputContainer button">
        <IconSectionSplitDown />
      </div>
      <div onClick={splitSectionHorizontal} className="inputContainer button">
        <IconSectionSplitLeft />
      </div>
      <div onClick={addNewSections} className="inputContainer button">
        <IconSectionAdd />
      </div>
      <OptionButtonStyled onClick={handleShowMatrixConfig}>
        <MatrixIcon />
      </OptionButtonStyled>
    </GridContainer>,
  ];

  const rotateFlipControlTools = [
    <GridContainer
      key="rotateFlipControlTools-grid"
      $gridTemplateColumns="1fr 1fr 1fr 1fr"
    >
      <Label key="rotateFlipControlTools-label-minus-90">- 90°</Label>
      <Label key="rotateFlipControlTools-label-plus-90">+ 90°</Label>
      <Label key="rotateFlipControlTools-label-horizontal">
        <FormattedMessage
          id="app.editor.sideMenu.expansionPanel.uploadImage.horizontal"
          defaultMessage="Horizontal"
        />
      </Label>
      {isFlipVerticalAvailable ? (
        <Label key="rotateFlipControlTools-label-vertical">
          <FormattedMessage
            id="app.editor.sideMenu.expansionPanel.uploadImage.vertical"
            defaultMessage="Vertical"
          />
        </Label>
      ) : (
        <div />
      )}
      <div onClick={rotateLeft} className="inputContainer button">
        <IconRotateLeft />
      </div>
      <div onClick={rotateRight} className="inputContainer button">
        <IconRotateRight />
      </div>
      <div onClick={flipHorizontal} className="inputContainer button">
        <IconFlipHorizontal />
      </div>
      {isFlipVerticalAvailable ? (
        <div onClick={flipVertical} className="inputContainer button">
          <IconFlipVertical />
        </div>
      ) : (
        <div />
      )}
    </GridContainer>,
  ];

  const matrixControlTools = [
    <GridContainer
      key="sectionsControlTools-grid-matrix"
      $gridTemplateColumns="1fr 1fr 1fr 1fr"
    >
      <Label key="sectionsControlTools-label-matrix-add-row-top">
        <FormattedMessage id="cmme.topRow" defaultMessage="Top row" />
      </Label>
      <Label key="sectionsControlTools-label-matrix-add-row-bottom">
        <FormattedMessage id="cmme.bottomRow" defaultMessage="Bottom row" />
      </Label>
      <Label key="sectionsControlTools-label-matrix-add-cell-left">
        <FormattedMessage id="cmme.leftCell" defaultMessage="Left cell" />
      </Label>
      <Label key="sectionsControlTools-label-matrix-add-cell-right">
        <FormattedMessage id="cmme.rightCell" defaultMessage="Right cell" />
      </Label>
      <div
        data-value="add-matrix-top-row"
        onClick={disable_appendMatrixRow ? null : matrixAddRowTop}
        className={
          disable_appendMatrixRow
            ? 'inputContainer button disabled'
            : 'inputContainer button'
        }
      >
        <IconMatrixAddRowTop
          disabled={disable_appendMatrixRow}
          fill={disable_appendMatrixRow ? colors.lightShadeGray : colors.nero}
        />
      </div>
      <div
        data-value="add-matrix-bottom-row"
        onClick={disable_appendMatrixRow ? null : matrixAddRowBottom}
        className={
          disable_appendMatrixRow
            ? 'inputContainer button disabled'
            : 'inputContainer button'
        }
      >
        <IconMatrixAddRowBottom
          disabled={disable_appendMatrixRow}
          fill={disable_appendMatrixRow ? colors.lightShadeGray : colors.nero}
        />
      </div>
      <div
        data-value="add-matrix-left-cell"
        onClick={disable_appendMatrixCell ? null : matrixAddCellLeft}
        className={
          disable_appendMatrixCell
            ? 'inputContainer button disabled'
            : 'inputContainer button'
        }
      >
        <IconMatrixAddCellLeft
          disabled={disable_appendMatrixCell}
          fill={disable_appendMatrixCell ? colors.lightShadeGray : colors.nero}
        />
      </div>
      <div
        data-value="add-matrix-right-cell"
        onClick={disable_appendMatrixCell ? null : matrixAddCellRight}
        className={
          disable_appendMatrixCell
            ? 'inputContainer button disabled'
            : 'inputContainer button'
        }
      >
        <IconMatrixAddCellRight
          disabled={disable_appendMatrixCell}
          fill={disable_appendMatrixCell ? colors.lightShadeGray : colors.nero}
        />
      </div>
    </GridContainer>,
    <GridContainer
      key="sectionsControlTools-grid-matrix-merge-unmerge-borders"
      $gridTemplateColumns="1fr 1fr 1fr 1fr"
    >
      <Label key="sectionsControlTools-label-matrix-merge">
        <FormattedMessage id="cmme.merge" defaultMessage="Merge" />
      </Label>
      <Label key="sectionsControlTools-label-matrix-unmerge">
        <FormattedMessage id="cmme.unmerge" defaultMessage="Unmerge" />
      </Label>
      <Label key="sectionsControlTools-label-matrix-borders">
        <FormattedMessage id="cmme.borders" defaultMessage="Borders" />
      </Label>
      <div></div>
      <div
        data-value="merge"
        onClick={disable_mergeMatrixCells ? null : matrixMerge}
        className={
          disable_mergeMatrixCells
            ? 'inputContainer button disabled'
            : 'inputContainer button'
        }
      >
        <IconMatrixMerge
          disabled={disable_mergeMatrixCells}
          fill={disable_mergeMatrixCells ? colors.lightShadeGray : colors.nero}
        />
      </div>
      <div
        data-value="unmerge"
        onClick={disable_splitMatrixCell ? null : matrixUnmerge}
        className={
          disable_splitMatrixCell
            ? 'inputContainer button disabled'
            : 'inputContainer button'
        }
      >
        <IconMatrixUnmerge
          disabled={disable_splitMatrixCell}
          fill={disable_splitMatrixCell ? colors.lightShadeGray : colors.nero}
        />
      </div>
      <div
        data-value="borders"
        onClick={toggleMatrixBorders}
        className={'inputContainer button'}
      >
        <IconMatrixBorders
          fill={
            selectedShield?.isMatrixBordersEnabled
              ? colors.primary
              : colors.nero
          }
        />
      </div>
      <div></div>
    </GridContainer>,
    <GridContainer key="sectionsControlTools-grid-matrix-width" $gridTemplateColumns="2fr 2fr">
      <Label key="sectionsControlTools-label-matrix-width">
        {`${intl.formatMessage({
          id: 'app.editor.sideMenu.panelLabel.borderWidth',
          defaultMessage: 'Width'
        })} (${sizeUnitTranslate})`}
      </Label>
      <div></div>
      <div className="inputContainer" id={'section-width-' + selectedSection?.id}>
        <UsefullNumericSizeInput
          className="inputContainer"
          $gridTemplateColumns="4fr 5fr 4fr"
          onChange={onMatrixCellWidthChangeClb}
          value={selectedSection ? (matrixRotation ? selectedSection.height : selectedSection.width) : 0}
          units={userSizeUnit}
          iconMinus={IconMinus}
          iconPlus={IconPlus}
          hoverColor={colors.whiteSmoke}
          selectedColor={colors.primary}
          disabled={disableMatrixCellWidth}
          stepMm={0.1}
          minval={0.1}
        />
      </div>
      <div></div>
    </GridContainer>
  ];

  const separatorControlTools = [
    <GridContainer
      key="sectionsControlTools-grid-separator"
      $gridTemplateColumns="1fr 1fr 1fr 1fr"
    >
      <Label key="sectionsControlTools-label-separator-add-left">
        <FormattedMessage
          id="cmme.leftSeparator"
          defaultMessage="Left separator"
        />
      </Label>
      <Label key="sectionsControlTools-label-separator-add-right">
        <FormattedMessage
          id="cmme.rightSeparator"
          defaultMessage="Right separator"
        />
      </Label>
      <div></div>
      <div></div>
      <div
        onClick={matrixAddSeparatorlLeft}
        className={`inputContainer button ${
          leftSeparatorDisabled ? 'disabled' : ''
        }`}
      >
        <IconMatrixAddSeparatorLeft
          fill={leftSeparatorDisabled ? colors.lightGray : null}
        />
      </div>
      <div
        onClick={matrixAddSeparatorlRight}
        className={`inputContainer button ${
          rightSeparatorDisabled ? 'disabled' : ''
        }`}
      >
        <IconMatrixAddSeparatorRight
          fill={rightSeparatorDisabled ? colors.lightGray : null}
        />
      </div>
      <div></div>
      <div></div>
    </GridContainer>,
  ];

  const shapeLineControlTools = [
    <GridContainer
      key="shapeLineControlTools-grid-stroke"
      $gridTemplateColumns="1fr 2fr 1fr"
    >
      <div>
        <Label key="shapeLineControlTools-label-stroke-toggle">
          <FormattedMessage id="cmme.stroke" defaultMessage="Stroke" />
        </Label>
      </div>
      {isShapeStrokeActive ? (
        <Label key="shapeLineControlTools-label-stroke-size">{`${intl.formatMessage(
          { id: 'cmme.size', defaultMessage: 'Size' }
        )} (${sizeUnitTranslate})`}</Label>
      ) : (
        <div></div>
      )}
      <div></div>

      <div className="inputContainer">
        <InlineButtonsGroup items={shapeStrokeToggleList} />
      </div>
      {isShapeStrokeActive ? (
        <UsefullNumericSizeInput
          className="inputContainer"
          $gridTemplateColumns="4fr 5fr 4fr"
          $borderRadius="4px"
          onChange={onShapeStrokeSizeChangeClb}
          value={shapeStrokeSize}
          units={userSizeUnit}
          iconMinus={IconMinus}
          iconPlus={IconPlus}
          hoverColor={colors.whiteSmoke}
          selectedColor={colors.primary}
          stepMm={0.1}
          minval={0.1}
        />
      ) : (
        <div></div>
      )}
      <div></div>
    </GridContainer>,
    ...(colorOptionAvailable
      ? [
          <GridContainer
            key="shapeLineControlTools-grid-stroke-color"
            $gridTemplateColumns="1fr"
          >
            <Label key="textControlTools-label-font-color">
              <FormattedMessage
                id="cmme.strokeColor"
                defaultMessage="Stroke color"
              />
            </Label>
            <div className="inputContainer">
              <ColorSelectionPanel
                onChange={handleShapeStrokeColorChange}
                onAddNewColor={() => {}}
                colorsList={null}
                additionalColors={[]}
                selected={selectedSection?.shapeStrokeColor}
                colorItemClassName="ColorItemRect"
              />
            </div>
          </GridContainer>,
        ]
      : []),
    ...(isShapeStrokeActive
      ? [
          <GridContainer
            key="shapeLineControlTools-grid-dash-type"
            $gridTemplateColumns="1fr 1fr"
          >
            <Label key="shapeLineControlTools-label-dash-type">
              <FormattedMessage
                id="app.editor.sideMenu.panelLabel.borderStyle"
                defaultMessage="Dash type"
              />
            </Label>
            <div></div>
            <div className="inputContainer">
              <InlineButtonsGroup items={shapeDashTypeList} />
            </div>
            <div></div>
          </GridContainer>,
        ]
      : []),
  ];

  const shapeSoftEdgesControlTools = [
    <GridContainer
      key="shapeLineControlTools-grid-soft-edges"
      $gridTemplateColumns="1fr 2fr 1fr"
    >
      <div>
        <Label key="shapeLineControlTools-label-soft-edges-toggle">
          <FormattedMessage
            id="app.editor.sideMenu.panelLabel.borderCornersStyle"
            defaultMessage="Soft edges"
          />
        </Label>
      </div>
      {isShapeRectangleSoftEdgesActive ? (
        <Label key="shapeLineControlTools-label-size">{`${intl.formatMessage({
          id: 'cmme.cornerRadius',
          defaultMessage: 'Corner radius',
        })} (${sizeUnitTranslate})`}</Label>
      ) : (
        <div></div>
      )}
      <div></div>

      <div className="inputContainer">
        <InlineButtonsGroup items={shapeRectangleSoftEdgesToggleList} />
      </div>
      {isShapeRectangleSoftEdgesActive ? (
        <UsefullNumericSizeInput
          className="inputContainer"
          $gridTemplateColumns="4fr 5fr 4fr"
          $borderRadius="4px"
          onChange={onShapeRectangleSoftEdgesSizeChangeClb}
          value={shapeRectangleSoftEdgesSize}
          units={userSizeUnit}
          iconMinus={IconMinus}
          iconPlus={IconPlus}
          hoverColor={colors.whiteSmoke}
          selectedColor={colors.primary}
          stepMm={DEFAULT_SHAPE_PARAMS.CORNER_RADIUS}
          minval={0.1}
        />
      ) : (
        <div></div>
      )}
      <div></div>
    </GridContainer>,
  ];

  const shapeFillControlTools = [
    <GridContainer
      key="shapeLineControlTools-grid-fill"
      $gridTemplateColumns="1fr 3fr"
    >
      <div>
        <Label key="shapeLineControlTools-label-fill-toggle">
          <FormattedMessage
            id="app.editor.menu.autofill.button.fill"
            defaultMessage="Fill"
          />
        </Label>
      </div>
      <div></div>

      <div className="inputContainer">
        <InlineButtonsGroup items={shapeFillToggleList} />
      </div>
      <div></div>
    </GridContainer>,
    ...(colorOptionAvailable && isShapeFillActive
      ? [
          <GridContainer
            key="shapeLineControlTools-grid-fill-color"
            $gridTemplateColumns="1fr"
          >
            <Label key="textControlTools-label-font-color">
              <FormattedMessage
                id="cmme.fillColor"
                defaultMessage="Fill color"
              />
            </Label>
            <div className="inputContainer">
              <ColorSelectionPanel
                onChange={handleShapeFillColorChange}
                onAddNewColor={() => {}}
                colorsList={null}
                additionalColors={[]}
                selected={selectedSection?.shapeFillColor}
                colorItemClassName="ColorItemRect"
              />
            </div>
          </GridContainer>,
        ]
      : []),
  ];

  const shapeRectangleOptionsControlTools = [
    ...shapeSoftEdgesControlTools,
    ...shapeLineControlTools,
    ...shapeFillControlTools,
  ];

  const viewControlTools = [
    ...gridControlTools,
    ...groupsSelectionControlTools,
  ];

  const graphicControlTools = [
    <GridContainer
      key="graphicControlTools-size"
      $gridTemplateColumns="1fr 1fr"
      $gridTemplateRows="max-content 50px"
    >
      <Label>
        {`${intl.formatMessage({
          id: 'cmme.size',
          defaultMessage: 'Size',
        })} (%)`}
      </Label>
      <div />
      <UsefullNumericSizeInput
        className="inputContainer"
        $borderRadius="4px"
        $gridTemplateColumns="4fr 5fr 4fr"
        onChange={handleGraphicFormattingSizeChange}
        value={graphicFormattingSizeValue}
        units={''}
        iconMinus={IconMinus}
        iconPlus={IconPlus}
        hoverColor={colors.whiteSmoke}
        selectedColor={colors.primary}
        stepMm={1}
        minval={1}
        maxval={100}
      />
      <div />
    </GridContainer>,
  ];

  if (!hasPages) {
    return <ItemsContainer id="sidebar-scrollable-container" />;
  } else if (isShowInsertModal) {
    return (
      <ItemsContainer id="sidebar-scrollable-container">
        <InsertBar
          isOpen={isShowInsertModal}
          selectedInsertBar={selectedInsertBar}
          setSelectedInsertBar={setSelectedInsertBar}
          isSmartPhone={false}
          selectedSection={selectedSection}
          isMatrix={isMatrix}
          horizontalAlignment={horizontalAlignment}
          verticalAlignment={verticalAlignment}
        />
      </ItemsContainer>
    );
  } else if (!areShieldsSelected && !areSectionsSelected) {
    return (
      <ItemsContainer id="sidebar-scrollable-container">
        <Expandable
          id="VIEW"
          translatedMessageID="cmme.view"
          defaultMessage="View"
          expanded={sideBarStates['VIEW']}
          visible={isVisible['VIEW']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={viewControlTools}
        />
      </ItemsContainer>
    );
  } else {
    return (
      <ItemsContainer id="sidebar-scrollable-container" onScroll={getScrollPos}>
        <Expandable
          id="GRAPHIC"
          translatedMessageID="cmme.graphicFormatting"
          defaultMessage="Graphic formatting"
          expanded={sideBarStates['GRAPHIC']}
          visible={isVisible['GRAPHIC']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={graphicControlTools}
        />
        <Expandable
          id="LASER"
          translatedMessageID="cmme.laserOutputQuality"
          defaultMessage="Laser output quality"
          expanded={sideBarStates['LASER']}
          visible={isVisible['LASER']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={laserControlTools}
        />
        <Expandable
          id="TEXT"
          translatedMessageID="app.editor.menu.format"
          defaultMessage="Text properties"
          expanded={sideBarStates['TEXT']}
          visible={isVisible['TEXT']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={textControlTools}
        />
        <Expandable
          id="SYMBOL"
          translatedMessageID="app.editor.sideMenu.expansionPanel.insertSymbols"
          defaultMessage="Insert symbol"
          expanded={sideBarStates['SYMBOL']}
          visible={isVisible['SYMBOL']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={<SymbolList />}
        />
        <Expandable
          id="SHAPERECTANGLE"
          translatedMessageID="cmme.rectangleOptions"
          defaultMessage="Rectangle options"
          expanded={sideBarStates['SHAPERECTANGLE']}
          visible={isVisible['SHAPERECTANGLE']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={shapeRectangleOptionsControlTools}
        />
        <Expandable
          id="SHAPEELLIPSE"
          translatedMessageID="cmme.ellipseOptions"
          defaultMessage="Ellipse options"
          expanded={sideBarStates['SHAPEELLIPSE']}
          visible={isVisible['SHAPEELLIPSE']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={[...shapeLineControlTools, ...shapeFillControlTools]}
        />
        <Expandable
          id="ALIGNMENT"
          translatedMessageID="app.editor.sideMenu.expansionPanel.uploadImage.alignment"
          defaultMessage="Alignment"
          expanded={sideBarStates['ALIGNMENT']}
          visible={isVisible['ALIGNMENT']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={aligmentControlTools}
        />
        <Expandable
          id="ROTATEFLIP"
          translatedMessageID="cmme.rotateFlip"
          defaultMessage="Rotate / flip"
          expanded={sideBarStates['ROTATEFLIP']}
          visible={isVisible['ROTATEFLIP']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={rotateFlipControlTools}
        />
        <Expandable
          id="MATRIX"
          translatedMessageID="cmme.addRowsAndCells"
          defaultMessage="Add rows and cells"
          expanded={sideBarStates['MATRIX']}
          visible={isVisible['MATRIX']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={matrixControlTools}
        />
        <Expandable
          id="SEPARATOR"
          translatedMessageID="app.editor.sideMenu.panelLabel.separator"
          defaultMessage="Separator"
          expanded={sideBarStates['SEPARATOR']}
          visible={isVisible['SEPARATOR']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={separatorControlTools}
        />
        <Expandable
          id="SECTIONS"
          translatedMessageID="cmme.sections"
          defaultMessage="Sections"
          expanded={sideBarStates['SECTIONS']}
          visible={isVisible['SECTIONS']}
          handleUpdateExpandableStates={handleUpdateExpandableStates}
          children={sectionsControlTools}
        />
        <Expandable
          id="VIEW"
          translatedMessageID="cmme.view"
          defaultMessage="View"
          expanded={isViewSectionOpenRef.current && sideBarStates['VIEW']}
          visible={isVisible['VIEW']}
          handleUpdateExpandableStates={handleViewSectionChange}
          children={viewControlTools}
        />
      </ItemsContainer>
    );
  }
};

export default React.memo(SideBar);
